import type { ComponentProps, KeyboardEvent } from "react"
import designSystemStyles from "@shared/styles/designSystemStyles"
import styles from "@shared/styles/components/LeftMenuOption.module.css"
import { HorizontalContainer } from "./Containers"
import { SystemIcon } from "./SystemIcon"
import { Text } from "./Texts"

export interface LeftMenuOptionProps {
    label: string
    icon: ComponentProps<typeof SystemIcon>["type"]
    isActive?: boolean
    isLocked?: boolean
    onClick?: () => void
    className?: string
}

export const LeftMenuOption: React.FC<LeftMenuOptionProps> = ({
    label,
    icon,
    isActive = false,
    isLocked = false,
    onClick,
    className
}) => {
    const containerClassName = [
        styles.option,
        isActive ? styles.active : "",
        isLocked ? styles.locked : "",
        onClick ? styles.clickable : "",
        "DSLeftMenuOption",
        className
    ].filter(Boolean).join(" ")
    const iconColor = isActive ? designSystemStyles.colorMainAction : designSystemStyles.colorText
    const labelColor = isActive ? designSystemStyles.colorMainAction : designSystemStyles.colorText
    const labelClassName = [styles.label].filter(Boolean).join(" ")

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!onClick) return
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            onClick()
        }
    }

    return (
        <div
            className={containerClassName}
            onClick={onClick}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={handleKeyDown}
        >
            <HorizontalContainer
                gap={designSystemStyles.gapSm}
                alignment="center"
                height={designSystemStyles.sizeButtonHeight}
                paddingLeft={designSystemStyles.paddingLg}
                paddingRight={designSystemStyles.paddingLg}
                paddingTop={designSystemStyles.paddingMd}
                paddingBottom={designSystemStyles.paddingMd}
            >
                <span className={styles.iconStack}>
                    <SystemIcon type={icon} color={iconColor} className={`DSLeftMenuOptionIcon ${styles.iconRegular}`} />
                    <SystemIcon type={icon} color={iconColor} weight="fill" className={`DSLeftMenuOptionIcon ${styles.iconFill}`} />
                </span>
                <Text text={label} color={labelColor} className={labelClassName} truncate={true} />
                {isLocked && (
                    <span className={styles.lockIcon}>
                        <SystemIcon type="lock" color={designSystemStyles.colorTextSecondary} size="small" />
                    </span>
                )}
            </HorizontalContainer>
        </div>
    )
}
